import React, { useCallback, useEffect, useRef, useState } from 'react';

const VIDEO_SRC = 'https://cdn.tiendaruna.io/Runamatic/devlanding.mp4';
const WATCH_THRESHOLD = 0.92;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Player liviano para /devlanding.
 * Desbloquea onWatched al completar ~92% del video (sin saltar adelante).
 */
const DevLandingVideoPlayer = ({ watched = false, onWatched }) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const unlockedRef = useRef(watched);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [maxWatched, setMaxWatched] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    unlockedRef.current = watched;
  }, [watched]);

  const markWatched = useCallback(() => {
    if (unlockedRef.current) return;
    unlockedRef.current = true;
    onWatched?.();
  }, [onWatched]);

  const revealControls = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) {
        setShowControls(false);
      }
    }, 2600);
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    revealControls();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
    revealControls();
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration || 0);
  };

  const clampToWatched = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime > maxWatchedRef.current) {
      video.currentTime = maxWatchedRef.current;
      setCurrent(maxWatchedRef.current);
    }
  }, []);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;

    const t = video.currentTime;

    // Salto brusco (>1s) = seek ilegal; la reproducción normal avanza de a poco.
    if (t > maxWatchedRef.current + 1) {
      video.currentTime = maxWatchedRef.current;
      setCurrent(maxWatchedRef.current);
      return;
    }

    setCurrent(t);

    if (t > maxWatchedRef.current) {
      maxWatchedRef.current = t;
      setMaxWatched(t);
      if (t / video.duration >= WATCH_THRESHOLD) {
        markWatched();
      }
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setShowControls(true);
    const video = videoRef.current;
    if (video && Number.isFinite(video.duration)) {
      maxWatchedRef.current = video.duration;
      setMaxWatched(video.duration);
    }
    markWatched();
  };

  const seekFromPointer = (clientX) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar || !duration) return;

    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const target = ratio * duration;

    // Solo retroceder o reposicionarse dentro de lo ya visto. Nunca adelantar.
    if (target > maxWatchedRef.current) {
      revealControls();
      return;
    }

    video.currentTime = target;
    setCurrent(target);
    revealControls();
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const clientX = e.clientX ?? e.changedTouches?.[0]?.clientX;
    if (clientX == null) return;
    seekFromPointer(clientX);
  };

  const progressPct = duration > 0 ? (current / duration) * 100 : 0;
  const watchedPct = duration > 0 ? (maxWatched / duration) * 100 : 0;

  return (
    <div className="dl-video" role="region" aria-label="Video explicativo">
      <p className="dl-video__label">Conocé la propuesta en 1:33</p>

      <div
        className={`dl-video__frame${playing && !showControls ? ' dl-video__frame--playing' : ''}`}
        onMouseMove={revealControls}
        onTouchStart={revealControls}
      >
        <video
          ref={videoRef}
          className="dl-video__el"
          src={VIDEO_SRC}
          playsInline
          preload="metadata"
          onPlay={() => {
            setPlaying(true);
            revealControls();
          }}
          onPause={() => {
            setPlaying(false);
            setShowControls(true);
          }}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={clampToWatched}
          onSeeked={clampToWatched}
          onEnded={handleEnded}
          onClick={togglePlay}
        />

        {!playing && (
          <button
            type="button"
            className="dl-video__play"
            onClick={togglePlay}
            aria-label="Reproducir video"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
            </svg>
          </button>
        )}

        <div
          className={`dl-video__controls${showControls || !playing ? ' dl-video__controls--visible' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="dl-video__ctrl"
            onClick={togglePlay}
            aria-label={playing ? 'Pausar' : 'Reproducir'}
          >
            {playing ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5.5v13l11-6.5-11-6.5z" />
              </svg>
            )}
          </button>

          <div
            ref={progressRef}
            className="dl-video__progress"
            role="slider"
            aria-label="Progreso del video"
            aria-valuemin={0}
            aria-valuemax={Math.round(maxWatched)}
            aria-valuenow={Math.round(current)}
            tabIndex={0}
            onClick={handleSeek}
            onTouchEnd={handleSeek}
            onKeyDown={(e) => {
              const video = videoRef.current;
              if (!video) return;
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                return;
              }
              if (e.key !== 'ArrowLeft') return;
              e.preventDefault();
              const next = Math.max(0, video.currentTime - 5);
              video.currentTime = next;
              setCurrent(next);
            }}
          >
            <div className="dl-video__progress-track" aria-hidden="true">
              <div className="dl-video__progress-watched" style={{ width: `${watchedPct}%` }} />
              <div className="dl-video__progress-played" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          <span className="dl-video__time" aria-hidden="true">
            {formatTime(current)} / {formatTime(duration)}
          </span>

          <button
            type="button"
            className="dl-video__ctrl"
            onClick={toggleMute}
            aria-label={muted ? 'Activar sonido' : 'Silenciar'}
          >
            {muted ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 10v4h3l4 3V7L7 10H4zm12.5 2l2.1-2.1 1.4 1.4L18 13.4l1.9 1.9-1.4 1.4L16.5 14.5l-2.1 2.1-1.4-1.4 2.1-2.1-2.1-2.1 1.4-1.4 2.1 2.1z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 10v4h3l4 3V7L7 10H4zm9.5 2a2.5 2.5 0 00-1.5-2.3v4.6a2.5 2.5 0 001.5-2.3zm-1.5-6.1v1.6a4.5 4.5 0 010 8.9v1.6a6 6 0 000-12.1z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <p className={`dl-video__hint${watched ? ' dl-video__hint--done' : ''}`}>
        {watched
          ? 'Video visto. Ya podés continuar con la cotización.'
          : 'Mira este breve video para entender nuestro servicio antes de reservar'}
      </p>
    </div>
  );
};

export default DevLandingVideoPlayer;
